import { openDB } from "idb";
import { reconstructEvents } from "../utils/history.js";

const DB_NAME = "media-tracker";
const DB_VERSION = 21;

// Map a store name to the normalized media_type token used across cross-media
// views (db/media.js). Groups carry the same token so a "book"-scoped group can
// be filtered into the Books list, a "videogame" one into Games, etc.
const STORE_MEDIA_TYPE = {
	games: "videogame",
	books: "book",
	tv: "tv",
	anime: "anime",
	movies: "movie",
	comics: "comic",
};

// Map a legacy playthrough_count into one of the new "times finished" buckets.
function bucketForCount(n) {
	if (n <= 1) return "1";
	if (n <= 10) return "2-10";
	if (n <= 20) return "11-20";
	if (n <= 50) return "21-50";
	return "51+";
}

// V1 → V2 key remaps. New keys use the "chatty" wording from PROJECT_STATUS.md.
const ENGAGEMENT_V1_TO_V2 = {
	backlog: "haven_started",
	active: "playing",
	finished: "done",
	// "Retired" meant "played a lot, walked away" — in the new model that's just
	// Done (the user defines what Done means).
	retired: "done",
	abandoned: "bounced",
	shelved: "on_pause",
	ongoing: "playing",
	// engagement=wishlist is a special case: it really means "want it, haven't
	// played" — split across the two new fields below.
	wishlist: "haven_started",
};

const OWNERSHIP_V1_TO_V2 = {
	owned: "have_it",
	// "Borrowed" and "Accessed" (Game Pass / library / etc.) both collapse into
	// the access-without-ownership bucket. We'd want a richer distinction later.
	borrowed: "friend_has_it",
	accessed: "friend_has_it",
	"loaned-out": "have_it",
	wishlist: "want_it",
	"previously-owned": "had_it",
	none: null,
};

function migrateGameToV2(game) {
	// Idempotent guard: a record that's already been migrated has media_type set.
	if (game.media_type) return game;

	const out = { ...game };
	out.legacy_engagement_status = game.engagement_status ?? null;
	out.legacy_ownership_status = game.ownership_status ?? null;

	const oldEng = game.engagement_status;
	const oldOwn = game.ownership_status;

	if (oldEng === "wishlist") {
		out.engagement_status = "haven_started";
		out.ownership_status = "want_it";
	} else {
		out.engagement_status = ENGAGEMENT_V1_TO_V2[oldEng] ?? "haven_started";
		out.ownership_status = OWNERSHIP_V1_TO_V2[oldOwn] ?? "have_it";
	}

	out.media_type = "videogame";
	out.franchise = game.franchise ?? null;
	out.collection = game.collection ?? null;
	// playthrough_count >= 2 is a strong signal the user actually replayed it,
	// so infer "came_back". Comfort pick stays opt-in.
	out.replay_signal =
		game.replay_signal ??
		((game.playthrough_count ?? 0) >= 2 ? "came_back" : null);

	return out;
}

let dbPromise = null;

export function getDB() {
	if (!dbPromise) {
		dbPromise = openDB(DB_NAME, DB_VERSION, {
			async upgrade(db, oldVersion, _newVersion, tx) {
				if (oldVersion < 1) {
					const games = db.createObjectStore("games", { keyPath: "id" });
					games.createIndex("igdb_id", "igdb_id", { unique: true });
					games.createIndex("engagement_status", "engagement_status");
					games.createIndex("ownership_status", "ownership_status");
					games.createIndex("date_added", "date_added");
					games.createIndex("last_engaged_date", "last_engaged_date");
					games.createIndex("name", "name");
				}
				if (oldVersion < 2) {
					const games = tx.objectStore("games");
					if (!games.indexNames.contains("franchise")) {
						games.createIndex("franchise", "franchise");
					}
					if (!games.indexNames.contains("collection")) {
						games.createIndex("collection", "collection");
					}
					if (!games.indexNames.contains("media_type")) {
						games.createIndex("media_type", "media_type");
					}

					if (!db.objectStoreNames.contains("groups")) {
						const groups = db.createObjectStore("groups", {
							keyPath: "id",
						});
						groups.createIndex("name", "name");
						// auto_key dedupes auto-groups: "franchise:Final Fantasy" etc.
						groups.createIndex("auto_key", "auto_key");
					}

					let cursor = await games.openCursor();
					while (cursor) {
						await cursor.update(migrateGameToV2(cursor.value));
						cursor = await cursor.continue();
					}
				}
				if (oldVersion < 3) {
					// `played_on` was repurposed from "platforms I played on" to
					// "platforms I own it on" and relabeled "Platforms" in the UI.
					// Rename the field so the data model matches its meaning.
					const games = tx.objectStore("games");
					let cursor = await games.openCursor();
					while (cursor) {
						const game = cursor.value;
						if (!("owned_on" in game)) {
							game.owned_on = game.played_on ?? [];
							delete game.played_on;
							await cursor.update(game);
						}
						cursor = await cursor.continue();
					}
				}
				if (oldVersion < 4) {
					// Replaced auto playthrough_count + replay_signal with a manual
					// "times finished" bucket. Seed it from any existing count so
					// replay history isn't lost, then drop the dead fields.
					const games = tx.objectStore("games");
					let cursor = await games.openCursor();
					while (cursor) {
						const game = cursor.value;
						if (!("times_finished" in game)) {
							const n = game.playthrough_count ?? 0;
							game.times_finished = n >= 1 ? bucketForCount(n) : null;
						}
						delete game.playthrough_count;
						delete game.replay_signal;
						await cursor.update(game);
						cursor = await cursor.continue();
					}
				}
				if (oldVersion < 5) {
					// VS Battle ranking: one record per media type, holding the
					// ranked order + any in-progress binary insertion.
					if (!db.objectStoreNames.contains("rankings")) {
						db.createObjectStore("rankings", { keyPath: "id" });
					}
				}
				if (oldVersion < 6) {
					// Books — a second media category (sourced from Open Library).
					// Kept in its own store so the games path is untouched. No data
					// migration; just create the store + indexes.
					if (!db.objectStoreNames.contains("books")) {
						const books = db.createObjectStore("books", {
							keyPath: "id",
						});
						books.createIndex("ol_id", "ol_id", { unique: true });
						books.createIndex("engagement_status", "engagement_status");
						books.createIndex("ownership_status", "ownership_status");
						books.createIndex("date_added", "date_added");
						books.createIndex("title", "title");
					}
				}
				if (oldVersion < 7) {
					// Align the local schema with the canonical (Supabase) model
					// ahead of cloud sync: rename fields, drop legacy cruft, add
					// forward-looking defaults. Media-specific fields stay FLAT in
					// their per-store records — the unified `media_extra` blob is
					// assembled at the Supabase boundary (db/cloud-items.js), not
					// here, so groups/views keep reading franchise/genres/authors/
					// etc. top-level and need no rewiring.
					const games = tx.objectStore("games");
					let gc = await games.openCursor();
					while (gc) {
						const g = gc.value;
						// name -> title, date_added -> created_at, date_updated -> updated_at
						if (!("title" in g)) g.title = g.name ?? null;
						delete g.name;
						if (!("created_at" in g)) g.created_at = g.date_added ?? null;
						delete g.date_added;
						if (!("updated_at" in g)) g.updated_at = g.date_updated ?? null;
						delete g.date_updated;
						// Drop legacy cruft (v1->v2 preserved these; never read since;
						// the rest are stray fields earlier migrations already removed).
						delete g.legacy_engagement_status;
						delete g.legacy_ownership_status;
						delete g.played_on;
						delete g.replay_signal;
						// Forward-looking canonical fields. playthrough_count is a
						// canonical column the app no longer uses (times_finished
						// replaced it in v4); kept at 0 so the cloud row is valid.
						g.playthrough_count = 0;
						if (!("visibility" in g)) g.visibility = "friends";
						await gc.update(g);
						gc = await gc.continue();
					}
					// games indexes: name -> title, date_added -> created_at.
					if (games.indexNames.contains("name")) games.deleteIndex("name");
					if (!games.indexNames.contains("title")) {
						games.createIndex("title", "title");
					}
					if (games.indexNames.contains("date_added")) {
						games.deleteIndex("date_added");
					}
					if (!games.indexNames.contains("created_at")) {
						games.createIndex("created_at", "created_at");
					}

					// books already use `title`; only the date fields + new defaults.
					const books = tx.objectStore("books");
					let bc = await books.openCursor();
					while (bc) {
						const b = bc.value;
						if (!("created_at" in b)) b.created_at = b.date_added ?? null;
						delete b.date_added;
						if (!("updated_at" in b)) b.updated_at = b.date_updated ?? null;
						delete b.date_updated;
						b.playthrough_count = 0;
						if (!("visibility" in b)) b.visibility = "friends";
						await bc.update(b);
						bc = await bc.continue();
					}
					if (books.indexNames.contains("date_added")) {
						books.deleteIndex("date_added");
					}
					if (!books.indexNames.contains("created_at")) {
						books.createIndex("created_at", "created_at");
					}

					// Sync queue: cloud-first writes that fail (offline) are queued
					// here and drained in the background (Phase 4). Created now to
					// avoid a second version bump later.
					if (!db.objectStoreNames.contains("pending_operations")) {
						const pending = db.createObjectStore("pending_operations", {
							keyPath: "id",
						});
						pending.createIndex("created_at", "created_at");
					}
				}
				if (oldVersion < 8) {
					// Repair step: `pending_operations` was added inside the v7 block
					// after some browsers had already reached v7, so its upgrade never
					// re-ran and the store was never created for them. Recreate it here
					// if missing. Idempotent — fresh installs already have it from v7.
					if (!db.objectStoreNames.contains("pending_operations")) {
						const pending = db.createObjectStore("pending_operations", {
							keyPath: "id",
						});
						pending.createIndex("created_at", "created_at");
					}
				}
				if (oldVersion < 9) {
					// Auto-groups (by franchise/collection/author) are retired — they
					// cluttered the list and buried hand-made groups (see db/groups.js,
					// where creation was removed). Clean up what's already here:
					//   • pure auto-groups (kind "auto") → deleted
					//   • edited ones (kind "hybrid")   → converted to plain manual
					//     groups, snapshotting their current members so curation
					//     survives but they stop auto-growing
					//   • manual groups                 → left untouched
					if (db.objectStoreNames.contains("groups")) {
						const matchesRule = (item, rule) => {
							if (!rule) return false;
							if (rule.type === "franchise")
								return item.franchise === rule.value;
							if (rule.type === "collection")
								return item.collection === rule.value;
							if (rule.type === "author")
								return (item.authors ?? []).includes(rule.value);
							return false;
						};

						// Current library, to resolve hybrid members at conversion time.
						let media = [];
						if (db.objectStoreNames.contains("games")) {
							media = media.concat(await tx.objectStore("games").getAll());
						}
						if (db.objectStoreNames.contains("books")) {
							media = media.concat(await tx.objectStore("books").getAll());
						}

						const groups = tx.objectStore("groups");
						let cursor = await groups.openCursor();
						while (cursor) {
							const g = cursor.value;
							if (g.kind === "auto") {
								await cursor.delete();
							} else if (g.kind === "hybrid") {
								const exclude = new Set(g.manual_exclude_ids ?? []);
								const manual = new Set(g.manual_member_ids ?? []);
								const memberIds = [];
								for (const m of media) {
									if (exclude.has(m.id)) continue;
									if (manual.has(m.id) || matchesRule(m, g.auto_rule)) {
										memberIds.push(m.id);
									}
								}
								g.manual_member_ids = memberIds;
								g.manual_exclude_ids = [];
								g.auto_rule = null;
								g.auto_key = null;
								g.kind = "manual";
								await cursor.update(g);
							}
							cursor = await cursor.continue();
						}
					}
				}
				if (oldVersion < 10) {
					// TV — the first hierarchical media type (series → seasons →
					// episodes). `tv` holds the synced series records (one row per
					// series; per-season/episode ratings + seen live in media_extra
					// override maps). `tv_catalog` is a local-only, re-fetchable cache of
					// the season/episode structure — never synced, never backed up.
					if (!db.objectStoreNames.contains("tv")) {
						const tv = db.createObjectStore("tv", { keyPath: "id" });
						tv.createIndex("tvmaze_id", "tvmaze_id", { unique: true });
						tv.createIndex("engagement_status", "engagement_status");
						tv.createIndex("ownership_status", "ownership_status");
						tv.createIndex("title", "title");
						tv.createIndex("created_at", "created_at");
					}
					if (!db.objectStoreNames.contains("tv_catalog")) {
						db.createObjectStore("tv_catalog", { keyPath: "tvmaze_id" });
					}
				}
				if (oldVersion < 11) {
					// Repair: the tv stores were introduced inside the v10 block, so a
					// browser that reached v10 *before* that block existed (e.g. a dev
					// page hot-reloaded mid-change) is stuck at v10 with no tv stores and
					// would never re-run v10. Recreate them here if missing — same hazard
					// and fix as the v7→v8 pending_operations repair. Idempotent.
					if (!db.objectStoreNames.contains("tv")) {
						const tv = db.createObjectStore("tv", { keyPath: "id" });
						tv.createIndex("tvmaze_id", "tvmaze_id", { unique: true });
						tv.createIndex("engagement_status", "engagement_status");
						tv.createIndex("ownership_status", "ownership_status");
						tv.createIndex("title", "title");
						tv.createIndex("created_at", "created_at");
					}
					if (!db.objectStoreNames.contains("tv_catalog")) {
						db.createObjectStore("tv_catalog", { keyPath: "tvmaze_id" });
					}
				}
				if (oldVersion < 12) {
					// Groups v2: groups become first-class "meta-items". Each gains a
					// settable status block (mirroring an item: engagement/ownership/
					// rating/awards/note), a `media_type` scope that decides which
					// category list it appears inline in (null = mixed/cross-media), a
					// `pinned_to_home` flag that decides home visibility, a
					// `child_group_ids` array for nesting, and `cover_item_id` to pick
					// which member's art represents the group. Purely additive; this
					// block only backfills defaults on existing groups.
					if (db.objectStoreNames.contains("groups")) {
						// Build an id -> media_type map so a group's scope can be inferred
						// from the media types its members happen to span.
						const typeById = new Map();
						for (const store of Object.keys(STORE_MEDIA_TYPE)) {
							if (!db.objectStoreNames.contains(store)) continue;
							for (const it of await tx.objectStore(store).getAll()) {
								typeById.set(it.id, STORE_MEDIA_TYPE[store]);
							}
						}

						const groups = tx.objectStore("groups");
						let cursor = await groups.openCursor();
						while (cursor) {
							const g = cursor.value;
							// Infer scope: a group whose members are all one media type is
							// scoped to that type; mixed or empty groups are cross-media.
							const memberTypes = new Set(
								(g.manual_member_ids ?? [])
									.map((id) => typeById.get(id))
									.filter(Boolean),
							);
							const mediaType =
								memberTypes.size === 1 ? [...memberTypes][0] : null;

							if (!("media_type" in g)) g.media_type = mediaType;
							// Cross-media groups default to the home screen (that's what the
							// old "Groups & lists" section surfaced); within-media groups
							// default off and live in their category list until pinned.
							if (!("pinned_to_home" in g))
								g.pinned_to_home = g.media_type === null;
							if (!("child_group_ids" in g)) g.child_group_ids = [];
							// First-class status block. `rating` may already exist (the
							// detail view let you rate a group), so only seed when absent.
							if (!("engagement_status" in g)) g.engagement_status = null;
							if (!("ownership_status" in g)) g.ownership_status = null;
							if (!("rating" in g)) g.rating = null;
							if (!("awards" in g)) g.awards = [];
							if (!("note" in g)) g.note = null;
							if (!("cover_item_id" in g)) g.cover_item_id = null;

							await cursor.update(g);
							cursor = await cursor.continue();
						}
					}
				}
				if (oldVersion < 13) {
					// Anime — a catalog-from-MAL/Jikan media type. Like TV it holds one
					// synced row per entry, but each MAL entry is one season/arc/movie
					// (MAL has no unified "series"), so there's no season tier:
					// `episode_overrides` are keyed by bare episode number and there's no
					// `season_overrides`. `anime_catalog` is the local-only, re-fetchable
					// episode-list cache (never synced/backed up), mirroring `tv_catalog`.
					if (!db.objectStoreNames.contains("anime")) {
						const anime = db.createObjectStore("anime", { keyPath: "id" });
						anime.createIndex("mal_id", "mal_id", { unique: true });
						anime.createIndex("engagement_status", "engagement_status");
						anime.createIndex("ownership_status", "ownership_status");
						anime.createIndex("title", "title");
						anime.createIndex("created_at", "created_at");
					}
					if (!db.objectStoreNames.contains("anime_catalog")) {
						db.createObjectStore("anime_catalog", { keyPath: "mal_id" });
					}
				}
				if (oldVersion < 14) {
					// Anime reworked from MAL-only to HYBRID: structure now comes from
					// TVmaze (series → seasons → episodes, same as TV), enriched with MAL
					// genres at add time. The record shape changes (mal_id → tvmaze_id,
					// season tier added), so the v13 stores are incompatible. They only
					// ever held day-one test data, so drop and recreate empty rather than
					// migrate. (Stale cloud rows with source='mal' are cleaned out with a
					// one-time `delete from items where media_type='anime';` — see
					// design_documents/anime-hybrid-rework.md.)
					if (db.objectStoreNames.contains("anime")) {
						db.deleteObjectStore("anime");
					}
					if (db.objectStoreNames.contains("anime_catalog")) {
						db.deleteObjectStore("anime_catalog");
					}
					const anime = db.createObjectStore("anime", { keyPath: "id" });
					anime.createIndex("tvmaze_id", "tvmaze_id", { unique: true });
					anime.createIndex("engagement_status", "engagement_status");
					anime.createIndex("ownership_status", "ownership_status");
					anime.createIndex("title", "title");
					anime.createIndex("created_at", "created_at");
					db.createObjectStore("anime_catalog", { keyPath: "tvmaze_id" });
				}
				if (oldVersion < 15) {
					// Notifications — local-only in-app alerts surfaced by the top-bar
					// bell (release-day arrivals for now; the store is intentionally
					// generic so other producers can add their own types later). Never
					// synced or backed up; cleared on sign-out with the user's data.
					// Keyed by a stable id (e.g. "release:<itemId>") so a producer can
					// dedupe. Only created_at is indexed — `read` is a boolean (not a
					// valid IndexedDB key), so unread counts filter in JS over the small
					// set.
					if (!db.objectStoreNames.contains("notifications")) {
						const notifs = db.createObjectStore("notifications", {
							keyPath: "id",
						});
						notifs.createIndex("created_at", "created_at");
					}
				}
				if (oldVersion < 16) {
					// Movies — the fifth media type (design_documents/movies-category.md).
					// One flat synced row per film, keyed by its IMDb id (the OMDb
					// external id, a string like "tt0120737"). No sub-items, so no
					// catalog cache store.
					if (!db.objectStoreNames.contains("movies")) {
						const movies = db.createObjectStore("movies", { keyPath: "id" });
						movies.createIndex("imdb_id", "imdb_id", { unique: true });
						movies.createIndex("engagement_status", "engagement_status");
						movies.createIndex("ownership_status", "ownership_status");
						movies.createIndex("title", "title");
						movies.createIndex("created_at", "created_at");
					}
				}
				if (oldVersion < 17) {
					// Comics — the sixth media type (design_documents/Comics/). One flat
					// synced row per issue or collected edition. Unlike every other type a
					// comic can come from TWO sources (Metron for single issues, Open
					// Library for trades), so the unique key is `comic_key` =
					// "<source>:<external_id>" ("metron:27305", "openlibrary:OL123W").
					if (!db.objectStoreNames.contains("comics")) {
						const comics = db.createObjectStore("comics", { keyPath: "id" });
						comics.createIndex("comic_key", "comic_key", { unique: true });
						comics.createIndex("engagement_status", "engagement_status");
						comics.createIndex("ownership_status", "ownership_status");
						comics.createIndex("title", "title");
						comics.createIndex("created_at", "created_at");
					}
				}
				if (oldVersion < 18) {
					// Comics creator view (design_documents/Comics/, Phase 3). All three
					// stores are device-local — never synced:
					//   • followed_creators   — the creators the user follows (user data:
					//     backed up, kept on sign-out like rankings). Keyed "<source>:<id>".
					//   • comic_issue_cache   — Metron issue detail per issue (credits →
					//     the creator's roles). Near-immutable, shared by every creator page.
					//   • creator_issue_lists — a creator's full issue list (list rows, no
					//     credits), refreshed when older than 14 days.
					if (!db.objectStoreNames.contains("followed_creators")) {
						const fc = db.createObjectStore("followed_creators", { keyPath: "creator_key" });
						fc.createIndex("added_at", "added_at");
					}
					if (!db.objectStoreNames.contains("comic_issue_cache")) {
						db.createObjectStore("comic_issue_cache", { keyPath: "issue_id" });
					}
					if (!db.objectStoreNames.contains("creator_issue_lists")) {
						db.createObjectStore("creator_issue_lists", { keyPath: "creator_id" });
					}
				}
				if (oldVersion < 19) {
					// Comic series subscriptions (design_documents/Comics/
					// series-subscriptions.md): one record per subscribed Metron series —
					// settings, seen issue/trade ids and the upcoming issues for the
					// release schedule. Device-local like followed_creators (backed up,
					// kept on sign-out, never synced).
					if (!db.objectStoreNames.contains("series_subscriptions")) {
						db.createObjectStore("series_subscriptions", { keyPath: "series_id" });
					}
				}
				if (oldVersion < 20) {
					// History (design_documents/history.md): the append-only activity log,
					// synced to Supabase `history_events`. Seeded once from the dates
					// items already carry (Added / Finished / Started), with
					// deterministic ids so every device's reconstruction converges on
					// one cloud copy. Pushed in a batch on the next signed-in launch.
					if (!db.objectStoreNames.contains("history")) {
						const history = db.createObjectStore("history", { keyPath: "id" });
						history.createIndex("at", "at");
						history.createIndex("item_id", "item_id");
						history.createIndex("kind", "kind");
						const byMedia = {};
						for (const [store, mediaType] of Object.entries(STORE_MEDIA_TYPE)) {
							if (db.objectStoreNames.contains(store)) {
								byMedia[mediaType] = await tx.objectStore(store).getAll();
							}
						}
						for (const ev of reconstructEvents(byMedia)) {
							await history.put({ ...ev, created_at: ev.at, updated_at: ev.at, _synced: false });
						}
					}
				}
				if (oldVersion < 21) {
					// People (design_documents/people.md): follows for every category in
					// one store, keyed "<source>:<person id>" ("metron:606",
					// "wikidata:Q4465", "tvmaze:31025", "openlibrary:OL79034A"). The
					// comics-only followed_creators rows move over unchanged (plus
					// person_key / media_type); the old store stays, unused.
					if (!db.objectStoreNames.contains("followed_people")) {
						const people = db.createObjectStore("followed_people", { keyPath: "person_key" });
						people.createIndex("media_type", "media_type");
						if (db.objectStoreNames.contains("followed_creators")) {
							for (const c of await tx.objectStore("followed_creators").getAll()) {
								await people.put({ ...c, person_key: c.creator_key, media_type: "comic" });
							}
						}
					}
				}
			},
			// If another tab holds an older-version connection, this open is blocked
			// and the upgrade never applies — the symptom is a persistent "object
			// store not found". Close our connection when we're the one blocking a
			// newer version, and warn when we're the one blocked, so it's diagnosable.
			blocked() {
				console.warn(
					"IndexedDB upgrade blocked by another open tab. Close other tabs of this app and reload.",
				);
			},
			blocking() {
				// We're holding an older-version connection that's blocking another
				// tab's upgrade. Close it and drop the cache so the upgrade can run;
				// the next getDB() in this tab reopens at the new version.
				console.warn("Closing DB connection so a newer version can upgrade.");
				const stale = dbPromise;
				dbPromise = null;
				stale?.then((db) => db.close()).catch(() => {});
			},
		});
	}
	return dbPromise;
}
